// LocalStorage-based progress tracking - no auth, kid-safe.
import { trackEvent } from "@/lib/analytics";
import { tickDaily } from "@/lib/dailyChallenge";
import { addDailyPoints } from "@/lib/dailyPoints";

const KEY = "we_progress_v1";

const DEFAULT = {
  stars: 0,
  badges: [], // ['first-quiz', 'continent-master', ...]
  streak: 0,
  lastPlayed: null, // ISO date
  countriesLearned: [], // country codes
  quizzesCompleted: 0,
  gamesWon: 0,
  coloringsCompleted: 0,
  bestSlide: {}, // landmarkId -> best (lowest) move count
  daily: { lastDate: null, streak: 0, lastScore: null }, // daily quiz state
  activity: [], // [{date, type, detail}]
  // Feb 2026: unified reward economy. Every quiz/quest/game funnels its
  // rewards into these two fields so kids see ONE comprehensive score
  // growing across the whole app — no separate "coins" wallet that
  // resets per session.
  coins: 0,          // lifetime coin total (persistent, adds across all games)
  bonusRounds: 0,    // how many bonus rounds the child has cashed in
  sessionCounts: {}, // { quiz: 3, "treasure-quest": 2, ... } — used for bonus-round trigger
  settings: {
    bigText: false,
    highContrast: false,
  },
};

export function loadProgress() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT, ...parsed, settings: { ...DEFAULT.settings, ...(parsed.settings || {}) } };
  } catch (e) {
    // Corrupted JSON or storage disabled. Fall back to defaults; log for diagnostics.
    console.warn("[progress] loadProgress fell back to defaults:", e);
    return { ...DEFAULT };
  }
}

export function saveProgress(p) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("we-progress-changed"));
    }
  } catch (e) {
    // Storage quota exceeded or storage disabled (e.g. Safari private mode).
    // Progress is in-memory for this session; log so it's visible in DevTools.
    console.warn("[progress] saveProgress failed (storage disabled or full):", e);
  }
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

const MILESTONE_STREAKS = new Set([3, 7, 14, 30, 100]);

export function updateStreak(p) {
  const today = todayStr();
  if (p.lastPlayed === today) return p;
  const before = p.streak || 0;
  if (p.lastPlayed) {
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    p.streak = p.lastPlayed === yesterday ? p.streak + 1 : 1;
  } else {
    p.streak = 1;
  }
  p.lastPlayed = today;
  // Feb 2026 · Cycle 63 — emit a milestone event on the first crossing
  // of 3/7/14/30/100 day streaks so a top-level listener can fire a
  // small `celebrate()` burst. Never repeats the same milestone.
  if (typeof window !== "undefined" && MILESTONE_STREAKS.has(p.streak) && before !== p.streak) {
    try { window.dispatchEvent(new CustomEvent("we-streak-milestone", { detail: { streak: p.streak } })); } catch { /* ignore */ }
  }
  return p;
}

export function addStars(n, reason = "") {
  const p = loadProgress();
  const wasZero = (p.stars || 0) === 0;
  p.stars += n;
  updateStreak(p);
  p.activity.unshift({ date: new Date().toISOString(), type: "star", detail: `+${n} ⭐ ${reason}` });
  p.activity = p.activity.slice(0, 50);
  saveProgress(p);
  try { trackEvent("stars_earned", { amount: n, reason: String(reason).slice(0, 60), total: p.stars }); } catch {/* ignore */}
  // Feb 2026 · Cycle 63 — celebrate the FIRST-ever star. Only fires
  // when the child crossed from zero to positive on THIS write, so a
  // returning session never re-triggers.
  if (typeof window !== "undefined" && wasZero && n > 0) {
    try { window.dispatchEvent(new CustomEvent("we-first-star", { detail: { total: p.stars } })); } catch { /* ignore */ }
  }
  // Return-loop hook: any meaningful star gain (≥2 ⭐, i.e. real
  // completion — not tiny nibbles) counts as an "activity completed"
  // moment. We fire this once per session so the PWA install prompt,
  // Tomorrow-hook and future celebrations can hang off a single event
  // without needing a per-activity integration.
  if (typeof window !== "undefined" && n >= 2) {
    try {
      const key = "we_activity_complete_fired";
      if (sessionStorage.getItem(key) !== "1") {
        sessionStorage.setItem(key, "1");
        window.dispatchEvent(new CustomEvent("we-activity-complete", { detail: { reason: String(reason).slice(0, 60), stars: n } }));
      }
    } catch { /* ignore */ }
  }
  return p;
}

export function awardBadge(id, label) {
  const p = loadProgress();
  if (!p.badges.find((b) => b.id === id)) {
    const wasEmpty = p.badges.length === 0;
    p.badges.push({ id, label, date: new Date().toISOString() });
    p.activity.unshift({ date: new Date().toISOString(), type: "badge", detail: `🏆 ${label}` });
    p.activity = p.activity.slice(0, 50);
    saveProgress(p);
    try { trackEvent("badge_earned", { id, label: String(label).slice(0, 60), total: p.badges.length }); } catch {/* ignore */}
    // Feb 2026 · Cycle 63 — celebrate the FIRST-ever badge. Also emit
    // a plain `we-badge-earned` for every badge so future non-first
    // milestones can chain without adding another entry point.
    if (typeof window !== "undefined") {
      try { window.dispatchEvent(new CustomEvent("we-badge-earned", { detail: { id, label } })); } catch { /* ignore */ }
      if (wasEmpty) {
        try { window.dispatchEvent(new CustomEvent("we-first-badge", { detail: { id, label } })); } catch { /* ignore */ }
      }
    }
  }
  return p;
}

export function isCountryLearned(code) {
  try { return loadProgress().countriesLearned.includes(code); } catch { return false; }
}

export function markCountryLearned(code) {
  const p = loadProgress();
  const isNew = !p.countriesLearned.includes(code);
  if (isNew) {
    p.countriesLearned.push(code);
    p.activity.unshift({ date: new Date().toISOString(), type: "learn", detail: `🌍 Learned ${code.toUpperCase()}` });
    p.activity = p.activity.slice(0, 50);
  }
  updateStreak(p);
  saveProgress(p);
  if (isNew && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("we-country-learned", { detail: { code } }));
    try { trackEvent("country_learned", { code, total: p.countriesLearned.length, streak: p.streak }); } catch {/* ignore */}
  }
  return p;
}

export function recordEvent(type, detail) {
  // Feb 2026 · Defensive coerce — some legacy callers passed an
  // object instead of (type, detail). Normalise so we never store
  // a non-string `type` field (which crashed Passport with React
  // error #31: "Objects are not valid as a React child").
  let safeType = type;
  let safeDetail = detail;
  if (typeof safeType === "object" && safeType !== null) {
    safeDetail = safeDetail || safeType;
    safeType = (safeType.type || safeType.name || "event");
  }
  safeType = String(safeType || "event").slice(0, 40);
  const p = loadProgress();
  if (safeType === "quiz") p.quizzesCompleted += 1;
  if (safeType === "game") p.gamesWon += 1;
  if (safeType === "coloring") p.coloringsCompleted += 1;
  updateStreak(p);
  p.activity.unshift({ date: new Date().toISOString(), type: safeType, detail: safeDetail });
  p.activity = p.activity.slice(0, 50);
  saveProgress(p);
  try { trackEvent(safeType, typeof safeDetail === "object" && safeDetail !== null ? safeDetail : { detail: String(safeDetail || "").slice(0, 64) }); } catch {/* ignore */}
  try { tickDaily(safeType); } catch {/* ignore */}
  return p;
}

export function updateSettings(next) {
  const p = loadProgress();
  p.settings = { ...p.settings, ...next };
  saveProgress(p);
  return p;
}

export function resetProgress() {
  localStorage.removeItem(KEY);
}

// Sliding-puzzle best moves: returns the lowest move count recorded for a landmark id.
export function getBestSlide(landmarkId) {
  const p = loadProgress();
  const v = (p.bestSlide || {})[landmarkId];
  return typeof v === "number" ? v : null;
}

// Update best slide moves only if `moves` is lower than the stored best.
// Returns { best: newBest, isNewRecord: bool }.
export function recordSlideMoves(landmarkId, moves) {
  const p = loadProgress();
  if (!p.bestSlide) p.bestSlide = {};
  const prev = p.bestSlide[landmarkId];
  const isNewRecord = prev == null || moves < prev;
  if (isNewRecord) {
    p.bestSlide[landmarkId] = moves;
    p.activity.unshift({
      date: new Date().toISOString(),
      type: "best",
      detail: `🏁 New best on ${landmarkId}: ${moves} moves`,
    });
    p.activity = p.activity.slice(0, 50);
    saveProgress(p);
  }
  return { best: p.bestSlide[landmarkId], isNewRecord };
}

// Daily quiz state -------------------------------------------------------------
const STREAK_CAP = 5;

// ─────────────────────────────────────────────────────────────
// Unified reward economy (Feb 2026)
// ─────────────────────────────────────────────────────────────
// One "World Points" number is derived from every reward the child
// earns across every activity. This is the single number Passport and
// share posters lead with — kids feel one big goal growing, not four
// disconnected wallets.
//
//   worldPoints = stars * 10 + coins + streak * 25
//               + badges.length * 50 + countries * 5
//
// Coins are now persistent. When a quiz/quest round ends, call
// `addCoins()` to bank the session total — it also grants a small
// number of stars proportionally (1 star per 20 coins, min 1) so the
// star meter still climbs alongside the coin bank.
export function addCoins(n, reason = "") {
  const amount = Math.max(0, Math.round(n));
  if (!amount) return loadProgress();
  const p = loadProgress();
  p.coins = (p.coins || 0) + amount;
  // Convert coins into star chunks so both meters advance together.
  // Round up so tiny wins (e.g. 5 coins) still bump the ⭐ number.
  const starDelta = Math.max(1, Math.floor(amount / 20));
  p.stars = (p.stars || 0) + starDelta;
  updateStreak(p);
  p.activity.unshift({
    date: new Date().toISOString(),
    type: "coins",
    detail: `+${amount} 🪙${starDelta ? `  · +${starDelta} ⭐` : ""} ${reason}`,
  });
  p.activity = p.activity.slice(0, 50);
  saveProgress(p);
  // Feb 2026: fan out to the Daily Points Tracker so milestone banners
  // fire regardless of which surface earned the points.
  try { addDailyPoints(amount, reason); } catch { /* ignore in tests */ }
  try { trackEvent("coins_earned", { amount, reason: String(reason).slice(0, 60), total: p.coins }); } catch { /* ignore */ }
  return p;
}

// One canonical "world points" number combining every reward type.
export function worldPoints(p) {
  const s = p || loadProgress();
  return (s.stars || 0) * 10
    + (s.coins || 0)
    + (s.streak || 0) * 25
    + (s.badges?.length || 0) * 50
    + (s.countriesLearned?.length || 0) * 5;
}

// ─────────────────────────────────────────────────────────────
// Bonus round scheduling (Feb 2026)
// ─────────────────────────────────────────────────────────────
// Every 3rd completed session of the same activity ("quiz", "treasure-
// quest", any custom key) unlocks a bonus round on the NEXT start.
// Sessions increment via `noteSession(key)`; ask `isBonusDue(key)` at
// the start of a new round to decide whether to double the payout.
// After the bonus round finishes, call `consumeBonus(key)` to reset.
export function noteSession(key) {
  const p = loadProgress();
  if (!p.sessionCounts) p.sessionCounts = {};
  p.sessionCounts[key] = (p.sessionCounts[key] || 0) + 1;
  saveProgress(p);
  return p.sessionCounts[key];
}

export function isBonusDue(key) {
  const p = loadProgress();
  const n = p.sessionCounts?.[key] || 0;
  // n counts *completed* rounds. Bonus should be the 4th round after 3
  // completed non-bonus rounds — i.e. when n >= 3 and n % 3 === 0.
  // After bonus is consumed we reset the counter (so bonuses feel
  // every-third-round, not increasingly rare).
  return n > 0 && n % 3 === 0;
}

export function consumeBonus(key) {
  const p = loadProgress();
  if (!p.sessionCounts) p.sessionCounts = {};
  p.sessionCounts[key] = 0;
  p.bonusRounds = (p.bonusRounds || 0) + 1;
  p.activity.unshift({
    date: new Date().toISOString(),
    type: "bonus",
    detail: `🎁 Bonus round played (${key})`,
  });
  p.activity = p.activity.slice(0, 50);
  saveProgress(p);
  return p;
}

export function getDailyState() {
  return loadProgress().daily || { lastDate: null, streak: 0, lastScore: null };
}

export function isDailyDoneToday() {
  return getDailyState().lastDate === todayStr();
}

// Mark the daily quiz complete. Adds 3 base stars + streak bonus stars.
// Returns { earned, streak, lastScore }.
export function completeDailyQuiz(score, total) {
  const p = loadProgress();
  const today = todayStr();
  const d = p.daily || { lastDate: null, streak: 0, lastScore: null };
  if (d.lastDate === today) {
    return { earned: 0, streak: d.streak, lastScore: d.lastScore, alreadyDone: true };
  }
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const nextStreak = d.lastDate === yesterday ? Math.min(d.streak + 1, STREAK_CAP * 4) : 1;
  const bonus = Math.min(nextStreak - 1, STREAK_CAP); // 0..5 bonus stars
  const base = 3;
  const earned = base + bonus;
  p.daily = { lastDate: today, streak: nextStreak, lastScore: `${score}/${total}` };
  p.stars += earned;
  updateStreak(p);
  p.activity.unshift({
    date: new Date().toISOString(),
    type: "daily",
    detail: `📅 Daily quiz ${score}/${total}: +${earned} ⭐ (streak ${nextStreak})`,
  });
  p.activity = p.activity.slice(0, 50);
  saveProgress(p);
  return { earned, streak: nextStreak, lastScore: `${score}/${total}`, bonus, alreadyDone: false };
}
